/**
 * Eshop url_overrides resolution (mirrors eshop/app/router.class.php AppRouter
 * and engine/modules/eshop_url_override.js).
 *
 * When a per-lang override is empty / true / null:
 * - home_url        → eshop.uri
 * - screenings_url  → {eshop.uri}/screening/all (+ ?lang= if ≠ default)
 * - articles_url    → {eshop.uri}/article/all (+ ?lang=)
 * - pass_url        → {eshop.uri}/pass/new (+ ?lang=)
 * - screening_url / article_url → no route fallback (empty)
 * - parent === false → disabled (empty string)
 *
 * Assumes sexy URLs (controller/action), same as typical rewrite.
 */

/**
 * @param {string} base
 * @param {string} path
 * @param {string} lang
 * @param {string} default_lang
 * @returns {string}
 */
function join_eshop_route(base, path, lang, default_lang) {
    if (!base)
        return '';
    const root = String(base).replace(/\/+$/, '');
    let url = `${root}/${path}`;
    if (lang && default_lang && lang !== default_lang)
        url += `?lang=${lang}`;
    return url;
}

/**
 * @param {Object} eshop
 * @param {string} key
 * @param {string} lang
 * @param {string} default_lang
 * @returns {string}
 */
function route_fallback(eshop, key, lang, default_lang) {
    const base = typeof eshop?.uri === 'string' ? eshop.uri : '';

    switch (key) {
        case 'home_url':
            return base || '';
        case 'screenings_url':
            return join_eshop_route(base, 'screening/all', lang, default_lang);
        case 'articles_url':
            return join_eshop_route(base, 'article/all', lang, default_lang);
        case 'pass_url':
            return join_eshop_route(base, 'pass/new', lang, default_lang);
        case 'screening_url':
        case 'article_url':
            return '';
        default:
            return '';
    }
}

/**
 * Resolve `url_overrides.{key}.{lang}` like Eshop AppRouter.
 *
 * @param {Object} eshop
 * @param {string} path_str e.g. "url_overrides.screenings_url.en"
 * @param {{ lang?: string, default_lang?: string }} [options]
 * @returns {string}
 */
export function resolve_eshop_url_override(eshop, path_str, options = {}) {
    if (!eshop || typeof path_str !== 'string' || !path_str.startsWith('url_overrides.'))
        return '';

    const rest = path_str.slice('url_overrides.'.length);
    const match = rest.match(/^([a-z0-9_]+)(?:\.([a-z]{2}))?$/i);
    if (!match)
        return '';

    const key = match[1];
    const lang = String(match[2] || options.lang || '').substring(0, 2).toLowerCase();
    const default_lang = String(options.default_lang || 'fr').substring(0, 2).toLowerCase();

    const parent = eshop.url_overrides?.[key];

    if (parent === false)
        return '';

    let value;
    if (lang && parent != null && typeof parent === 'object' && !Array.isArray(parent))
        value = parent[lang];
    else if (typeof parent === 'string' || typeof parent === 'boolean')
        value = parent;
    else
        value = undefined;

    if (value === false)
        return '';

    if (typeof value === 'string' && value.trim() !== '')
        return value;

    return route_fallback(eshop, key, lang, default_lang);
}
